import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { useLanguage } from '@/lib/LanguageContext';

export default function Footer() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <footer className="bg-gray-950 pt-16 pb-20 lg:pb-8 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1 space-y-5">
            <Logo variant="light" imgClassName="h-[2.925rem]" invert loading="lazy" />
            <p className="text-gray-500 text-sm leading-relaxed font-light">
              {t('footer.tagline')}
              <br />
              <span className="text-blue-400 font-medium">{t('footer.taglineHighlight')}</span> {t('footer.taglineEnd')}
            </p>
            <div className="flex gap-3">
              {[
                { icon: Instagram, href: 'https://instagram.com/smartpos.es', label: 'Instagram' },
                { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61592711784601', label: 'Facebook' },
                { icon: Linkedin, href: 'https://www.linkedin.com/company/smartpos-development/', label: 'LinkedIn' },
                { icon: Youtube, href: 'https://www.youtube.com/@smartposdevelopment7376', label: 'YouTube' },
              ].map(({ icon: Icon, href, label }) => (
                <a key={href} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center text-gray-500 hover:bg-blue-500/20 hover:text-blue-400 transition-all">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Soluciones */}
          <div>
            <h2 className="text-white font-black text-sm mb-4">{t('footer.solutions')}</h2>
            <ul className="space-y-2.5">
              {[
                { label: t('footer.software'), to: '/tpv-nube' },
                { label: t('footer.hardware'), to: '/hardware-tpv' },
                { label: t('footer.cashdro'), to: '/cashdro' },
                { label: t('footer.kiosks'), to: '/quioscos-autoservicio' },
                { label: t('footer.verifactu'), to: '/verifactu' },
              ].map(item => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className="text-gray-500 hover:text-white text-sm transition-colors text-left font-light">
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/tecnicos"
                  className="text-gray-500 hover:text-white text-sm transition-colors text-left font-light"
                >
                  {t('footer.technicians')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Sectores */}
          <div>
            <h2 className="text-white font-black text-sm mb-4">{t('footer.sectors')}</h2>
            <ul className="space-y-2.5">
              {[
                { label: t('footer.hospitality'), to: '/hosteleria' },
                { label: t('footer.retail'), to: '/retail' },
                { label: t('footer.food'), to: '/alimentacion' },
                { label: t('footer.beauty'), to: '/belleza' },
                { label: t('footer.franchises'), to: '/cadenas-franquicias' },
              ].map(item => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    className="text-gray-500 hover:text-white text-sm transition-colors text-left font-light">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h2 className="text-white font-black text-sm mb-4">{t('footer.contact')}</h2>
            <ul className="space-y-3">
              <li>
                <a href="tel:+34936302521" className="flex items-start gap-2.5 text-gray-500 hover:text-white transition-colors">
                  <Phone size={14} className="text-blue-500 mt-0.5 shrink-0" />
                  <span className="text-sm font-medium">+34 936 302 521</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@smartpos.es" className="flex items-start gap-2.5 text-gray-500 hover:text-white transition-colors">
                  <Mail size={14} className="text-blue-500 mt-0.5 shrink-0" />
                  <span className="text-sm font-light">info@smartpos.es</span>
                </a>
              </li>
              <li>
                <a href="https://www.google.com/maps/search/?api=1&query=Av.+Llu%C3%ADs+Companys+23+08970+Sant+Joan+Desp%C3%AD+Barcelona" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2.5 text-gray-500 hover:text-white transition-colors">
                  <MapPin size={14} className="text-blue-500 mt-0.5 shrink-0" />
                  <span className="text-sm font-light">Av. Lluís Companys, 23, Local A<br />08970 Sant Joan Despí, Barcelona, Spain</span>
                </a>
              </li>
            </ul>
            <button
              onClick={() => navigate('/demo')}
              className="mt-5 w-full bg-brand-blue text-white py-3 rounded-xl font-bold text-sm hover:bg-[#0078d4] transition-all shadow-lg shadow-blue-500/20"
            >
              {t('footer.freeDemo')}
            </button>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-xs">
            © {new Date().getFullYear()} SmartPOS. {t('footer.rights')}
          </p>
          <div className="flex flex-wrap gap-5 justify-center">
            <Link to="/sobre-nosotros" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.about')}</Link>
            <Link to="/blog" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.blog')}</Link>
            <Link to="/precios" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.pricing')}</Link>
            <Link to="/contacto" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.contact')}</Link>
            <Link to="/guia-tpv" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.guide')}</Link>
            <Link to="/legal/privacidad" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.privacy')}</Link>
            <Link to="/legal/cookies" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.cookies')}</Link>
            <Link to="/legal/aviso-legal" className="text-gray-400 hover:text-white text-xs transition-colors">{t('footer.legal')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}